'use client';

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

type TrialAbuseRestrictionCoordinatorValue = Readonly<{
  activeRequestId?: string;
  dismissVisibleRequests: () => void;
  setRequestVisible: (requestId: string, isVisible: boolean) => void;
}>;

const TrialAbuseRestrictionCoordinatorContext =
  createContext<TrialAbuseRestrictionCoordinatorValue | null>(null);

/**
 * Ensures overlapping lockout surfaces share one restriction modal lifecycle.
 */
export function TrialAbuseRestrictionCoordinator({
  children,
}: Readonly<{ children: ReactNode }>) {
  const [visibleRequestIds, setVisibleRequestIds] = useState<readonly string[]>(
    [],
  );
  const [dismissedRequestIds, setDismissedRequestIds] = useState<
    ReadonlySet<string>
  >(() => new Set());

  const setRequestVisible = useCallback(
    (requestId: string, isVisible: boolean) => {
      setVisibleRequestIds((currentRequestIds) => {
        if (isVisible) {
          return currentRequestIds.includes(requestId)
            ? currentRequestIds
            : [...currentRequestIds, requestId];
        }

        return currentRequestIds.filter(
          (currentRequestId) => currentRequestId !== requestId,
        );
      });

      if (!isVisible) {
        setDismissedRequestIds((currentRequestIds) => {
          if (!currentRequestIds.has(requestId)) return currentRequestIds;

          const nextRequestIds = new Set(currentRequestIds);
          nextRequestIds.delete(requestId);
          return nextRequestIds;
        });
      }
    },
    [],
  );

  const dismissVisibleRequests = useCallback(() => {
    setDismissedRequestIds(
      (currentRequestIds) =>
        new Set([...currentRequestIds, ...visibleRequestIds]),
    );
  }, [visibleRequestIds]);

  const value = useMemo(
    () => ({
      activeRequestId: visibleRequestIds.find(
        (requestId) => !dismissedRequestIds.has(requestId),
      ),
      dismissVisibleRequests,
      setRequestVisible,
    }),
    [
      dismissVisibleRequests,
      dismissedRequestIds,
      setRequestVisible,
      visibleRequestIds,
    ],
  );

  return (
    <TrialAbuseRestrictionCoordinatorContext.Provider value={value}>
      {children}
    </TrialAbuseRestrictionCoordinatorContext.Provider>
  );
}

export const useTrialAbuseRestrictionCoordinator = () =>
  useContext(TrialAbuseRestrictionCoordinatorContext);
